import { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import AdminLayout from '../../components/admin/AdminLayout';
import { apiRequest } from '../../utils/api';

const AnalyticsPage = () => {
    const { organizationId } = useParams();
    const [stats, setStats] = useState(null);
    const [hits, setHits] = useState([]);
    const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, total_pages: 0 });
    const [categoryPagination, setCategoryPagination] = useState({ page: 1, limit: 20, total: 0, total_pages: 0 });
    const [productPagination, setProductPagination] = useState({ page: 1, limit: 20, total: 0, total_pages: 0 });
    const [actionPagination, setActionPagination] = useState({ page: 1, limit: 20, total: 0, total_pages: 0 });
    const [campaignPagination, setCampaignPagination] = useState({ page: 1, limit: 20, total: 0, total_pages: 0 });
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('overview');
    const [dateRange, setDateRange] = useState({
        start: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        end: new Date().toISOString().split('T')[0]
    });
    
    // Detailed reports data
    const [categoryStats, setCategoryStats] = useState([]);
    const [productStats, setProductStats] = useState([]);
    const [deviceStats, setDeviceStats] = useState([]);
    const [actionStats, setActionStats] = useState([]);
    const [campaignStats, setCampaignStats] = useState([]);
    
    // Smart table states (search, sort)
    const [searchTerm, setSearchTerm] = useState('');
    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

    useEffect(() => {
        loadAnalytics();
    }, [organizationId, dateRange, pagination.page]);

    useEffect(() => {
        // Reset search and sort when tab changes
        setSearchTerm('');
        setSortConfig({ key: null, direction: 'asc' });
        
        if (activeTab === 'categories') {
            loadCategoryStats();
        } else if (activeTab === 'products') {
            loadProductStats();
        } else if (activeTab === 'devices') {
            loadDeviceStats();
        } else if (activeTab === 'actions') {
            loadActionStats();
        } else if (activeTab === 'campaigns') {
            loadCampaignStats();
        }
    }, [activeTab, dateRange, organizationId, categoryPagination.page, productPagination.page, actionPagination.page, campaignPagination.page]);

    const loadAnalytics = async () => {
        setLoading(true);
        try {
            const [statsResponse, hitsResponse] = await Promise.all([
                apiRequest(`/organizations/${organizationId}/analytics/statistics?start_date=${dateRange.start}&end_date=${dateRange.end}`).catch(() => ({ data: {} })),
                apiRequest(`/organizations/${organizationId}/analytics/hits?start_date=${dateRange.start}&end_date=${dateRange.end}&page=${pagination.page}&limit=20`).catch(() => ({ data: [], pagination: { page: 1, limit: 20, total: 0, total_pages: 0 } }))
            ]);

            setStats(statsResponse.data || {});
            setHits(hitsResponse.data || []);
            setPagination(prev => ({
                ...prev,
                ...(hitsResponse.pagination || {})
            }));
        } catch (error) {
            console.error('Load analytics error:', error);
        } finally {
            setLoading(false);
        }
    };

    const loadCategoryStats = async () => {
        try {
            const response = await apiRequest(`/organizations/${organizationId}/analytics/categories?start_date=${dateRange.start}&end_date=${dateRange.end}&page=${categoryPagination.page}&limit=20`);
            setCategoryStats(response.data || []);
            setCategoryPagination(prev => ({
                ...prev,
                ...(response.pagination || {})
            }));
        } catch (error) {
            console.error('Load category stats error:', error);
        }
    };

    const loadProductStats = async () => {
        try {
            const response = await apiRequest(`/organizations/${organizationId}/analytics/products?start_date=${dateRange.start}&end_date=${dateRange.end}&page=${productPagination.page}&limit=20`);
            setProductStats(response.data || []);
            setProductPagination(prev => ({
                ...prev,
                ...(response.pagination || {})
            }));
        } catch (error) {
            console.error('Load product stats error:', error);
        }
    };

    const loadDeviceStats = async () => {
        try {
            const response = await apiRequest(`/organizations/${organizationId}/analytics/devices?start_date=${dateRange.start}&end_date=${dateRange.end}`);
            setDeviceStats(response.data || []);
        } catch (error) {
            console.error('Load device stats error:', error);
        }
    };

    const loadActionStats = async () => {
        try {
            const response = await apiRequest(`/organizations/${organizationId}/analytics/actions?start_date=${dateRange.start}&end_date=${dateRange.end}&page=${actionPagination.page}&limit=20`);
            setActionStats(response.data || []);
            setActionPagination(prev => ({
                ...prev,
                ...(response.pagination || {})
            }));
        } catch (error) {
            console.error('Load action stats error:', error);
        }
    };

    const loadCampaignStats = async () => {
        try {
            const response = await apiRequest(`/organizations/${organizationId}/analytics/campaigns?start_date=${dateRange.start}&end_date=${dateRange.end}&page=${campaignPagination.page}&limit=20`);
            setCampaignStats(response.data || []);
            setCampaignPagination(prev => ({
                ...prev,
                ...(response.pagination || {})
            }));
        } catch (error) {
            console.error('Load campaign stats error:', error);
        }
    };

    const getEntityTypeLabel = (type) => {
        const labels = {
            menu: 'Ana Sayfa',
            product: 'Ürün',
            category: 'Kategori',
            banner: 'Banner',
            campaign: 'Kampanya',
            whatsapp: 'WhatsApp',
            phone: 'Telefon',
            contact: 'Mesaj'
        };
        return labels[type] || type;
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return '-';
        return new Date(dateStr).toLocaleDateString('tr-TR', {
            day: 'numeric',
            month: 'short',
            year: 'numeric'
        });
    };

    // Smart table functions
    const handleSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
    };

    const exportToCSV = (data, filename) => {
        if (!data || data.length === 0) {
            alert('Dışa aktarılacak veri yok');
            return;
        }

        const headers = Object.keys(data[0]);
        const csvContent = [
            headers.join(','),
            ...data.map(row => 
                headers.map(header => {
                    const value = row[header];
                    if (value === null || value === undefined) return '';
                    if (typeof value === 'object') return JSON.stringify(value);
                    return String(value).replace(/"/g, '""');
                }).join(',')
            )
        ].join('\n');

        const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', `${filename}_${new Date().toISOString().split('T')[0]}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    // Filter and sort data
    const getFilteredAndSortedData = (data) => {
        if (!data || data.length === 0) return [];
        
        let filtered = data;
        
        // Apply search filter
        if (searchTerm) {
            filtered = filtered.filter(item => {
                return Object.values(item).some(value => 
                    value && String(value).toLowerCase().includes(searchTerm.toLowerCase())
                );
            });
        }
        
        // Apply sorting
        if (sortConfig.key) {
            filtered = [...filtered].sort((a, b) => {
                const aVal = a[sortConfig.key];
                const bVal = b[sortConfig.key];
                
                if (aVal === null || aVal === undefined) return 1;
                if (bVal === null || bVal === undefined) return -1;
                
                if (typeof aVal === 'number' && typeof bVal === 'number') {
                    return sortConfig.direction === 'asc' ? aVal - bVal : bVal - aVal;
                }
                
                const aStr = String(aVal).toLowerCase();
                const bStr = String(bVal).toLowerCase();
                
                if (sortConfig.direction === 'asc') {
                    return aStr.localeCompare(bStr, 'tr');
                } else {
                    return bStr.localeCompare(aStr, 'tr');
                }
            });
        }
        
        return filtered;
    };

    // Memoized filtered data
    const filteredHits = useMemo(() => getFilteredAndSortedData(hits), [hits, searchTerm, sortConfig]);
    const filteredCategoryStats = useMemo(() => getFilteredAndSortedData(categoryStats), [categoryStats, searchTerm, sortConfig]);
    const filteredProductStats = useMemo(() => getFilteredAndSortedData(productStats), [productStats, searchTerm, sortConfig]);
    const filteredCampaignStats = useMemo(() => getFilteredAndSortedData(campaignStats), [campaignStats, searchTerm, sortConfig]);
    const filteredActionStats = useMemo(() => getFilteredAndSortedData(actionStats), [actionStats, searchTerm, sortConfig]);
    const filteredDeviceStats = useMemo(() => getFilteredAndSortedData(deviceStats), [deviceStats, searchTerm, sortConfig]);

    return (
        <AdminLayout>
            <div className="space-y-6">
                <div className="flex items-center justify-between">
                    <h1 className="text-3xl font-bold text-gray-900">İstatistikler</h1>
                    
                    {/* Date Range Selector */}
                    <div className="flex items-center space-x-4">
                        <input
                            type="date"
                            value={dateRange.start}
                            onChange={(e) => setDateRange(prev => ({ ...prev, start: e.target.value }))}
                            className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                        />
                        <span className="text-gray-500">-</span>
                        <input
                            type="date"
                            value={dateRange.end}
                            onChange={(e) => setDateRange(prev => ({ ...prev, end: e.target.value }))}
                            className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                        />
                    </div>
                </div>

                {loading && !stats ? (
                    <div className="flex items-center justify-center py-12">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600"></div>
                    </div>
                ) : (
                    <>
                        {/* Statistics Cards */}
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <p className="text-sm font-medium text-gray-600">Toplam Görüntüleme</p>
                                        <p className="text-3xl font-bold text-gray-900 mt-2">{stats?.total || 0}</p>
                                    </div>
                                    <div className="w-16 h-16 bg-blue-100 rounded-xl flex items-center justify-center">
                                        <span className="text-3xl">👁️</span>
                                    </div>
                                </div>
                            </div>

                            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <p className="text-sm font-medium text-gray-600">Benzersiz Ziyaretçi</p>
                                        <p className="text-3xl font-bold text-gray-900 mt-2">{stats?.unique_visitors || 0}</p>
                                    </div>
                                    <div className="w-16 h-16 bg-green-100 rounded-xl flex items-center justify-center">
                                        <span className="text-3xl">👤</span>
                                    </div>
                                </div>
                            </div>

                            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <p className="text-sm font-medium text-gray-600">Ürün Görüntüleme</p>
                                        <p className="text-3xl font-bold text-gray-900 mt-2">{stats?.product_hits || 0}</p>
                                    </div>
                                    <div className="w-16 h-16 bg-purple-100 rounded-xl flex items-center justify-center">
                                        <span className="text-3xl">🍕</span>
                                    </div>
                                </div>
                            </div>

                            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <p className="text-sm font-medium text-gray-600">Kategori Görüntüleme</p>
                                        <p className="text-3xl font-bold text-gray-900 mt-2">{stats?.category_hits || 0}</p>
                                    </div>
                                    <div className="w-16 h-16 bg-yellow-100 rounded-xl flex items-center justify-center">
                                        <span className="text-3xl">📁</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Tabs */}
                        <div className="border-b border-gray-200">
                            <nav className="flex space-x-8">
                                {[
                                    { id: 'overview', name: 'Genel Bakış' },
                                    { id: 'categories', name: 'Kategoriler' },
                                    { id: 'products', name: 'Ürünler' },
                                    { id: 'campaigns', name: 'Kampanyalar' },
                                    { id: 'devices', name: 'Cihazlar' },
                                    { id: 'actions', name: 'Aksiyonlar' }
                                ].map((tab) => (
                                    <button
                                        key={tab.id}
                                        onClick={() => setActiveTab(tab.id)}
                                        className={`py-3 px-1 border-b-2 font-medium text-sm transition-colors ${
                                            activeTab === tab.id
                                                ? 'border-orange-500 text-orange-600'
                                                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                        }`}
                                    >
                                        {tab.name}
                                    </button>
                                ))}
                            </nav>
                        </div>

                        {/* Tab Content */}
                        {activeTab === 'overview' && (
                            <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                                <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                                    <h2 className="text-lg font-semibold text-gray-900">Son Görüntülemeler</h2>
                                    <div className="flex items-center gap-3">
                                        <input
                                            type="text"
                                            placeholder="Ara..."
                                            value={searchTerm}
                                            onChange={(e) => setSearchTerm(e.target.value)}
                                            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
                                        />
                                        <button
                                            onClick={() => exportToCSV(filteredHits, 'goruntulemeler')}
                                            className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition text-sm font-medium"
                                        >
                                            📥 Dışa Aktar
                                        </button>
                                    </div>
                                </div>
                                {hits.length === 0 ? (
                                    <div className="text-center py-12">
                                        <p className="text-gray-500">Bu tarih aralığında görüntüleme bulunamadı.</p>
                                    </div>
                                ) : (
                                    <>
                                        <div className="overflow-x-auto">
                                            <table className="min-w-full divide-y divide-gray-200">
                                                <thead className="bg-gray-50">
                                                    <tr>
                                                        <th 
                                                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                                                            onClick={() => handleSort('created_at')}
                                                        >
                                                            <div className="flex items-center gap-2">
                                                                Tarih
                                                                {sortConfig.key === 'created_at' && (
                                                                    <span>{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>
                                                                )}
                                                            </div>
                                                        </th>
                                                        <th 
                                                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                                                            onClick={() => handleSort('entity_type')}
                                                        >
                                                            <div className="flex items-center gap-2">
                                                                Tip
                                                                {sortConfig.key === 'entity_type' && (
                                                                    <span>{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>
                                                                )}
                                                            </div>
                                                        </th>
                                                        <th 
                                                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                                                            onClick={() => handleSort('entity_id')}
                                                        >
                                                            <div className="flex items-center gap-2">
                                                                ID
                                                                {sortConfig.key === 'entity_id' && (
                                                                    <span>{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>
                                                                )}
                                                            </div>
                                                        </th>
                                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                            IP Adresi
                                                        </th>
                                                    </tr>
                                                </thead>
                                                <tbody className="bg-white divide-y divide-gray-200">
                                                    {filteredHits.map((hit) => (
                                                        <tr key={hit.id} className="hover:bg-gray-50">
                                                            <td className="px-6 py-4 whitespace-nowrap">
                                                                <div className="text-sm text-gray-900">
                                                                    {new Date(hit.created_at).toLocaleString('tr-TR')}
                                                                </div>
                                                            </td>
                                                            <td className="px-6 py-4 whitespace-nowrap">
                                                                <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                                                                    {hit.entity_type ? getEntityTypeLabel(hit.entity_type) : '-'}
                                                                </span>
                                                            </td>
                                                            <td className="px-6 py-4 whitespace-nowrap">
                                                                <div className="text-sm text-gray-900">
                                                                    {hit.entity_type && ['whatsapp', 'phone', 'contact', 'menu'].includes(hit.entity_type)
                                                                        ? getEntityTypeLabel(hit.entity_type)
                                                                        : hit.entity_id && hit.entity_name 
                                                                            ? `#${hit.entity_id} ${hit.entity_name}`
                                                                            : hit.entity_id 
                                                                                ? `#${hit.entity_id}`
                                                                                : '-'}
                                                                </div>
                                                            </td>
                                                            <td className="px-6 py-4 whitespace-nowrap">
                                                                <div className="text-sm text-gray-900">{hit.user_ip || '-'}</div>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                        
                                        {/* Pagination */}
                                        {pagination.total_pages > 1 && (
                                            <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
                                                <div className="text-sm text-gray-700">
                                                    Toplam <span className="font-medium">{pagination.total}</span> kayıt
                                                </div>
                                                <div className="flex items-center space-x-2">
                                                    <button
                                                        onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                                                        disabled={pagination.page === 1}
                                                        className="px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                                    >
                                                        Önceki
                                                    </button>
                                                    <span className="text-sm text-gray-700">
                                                        Sayfa {pagination.page} / {pagination.total_pages}
                                                    </span>
                                                    <button
                                                        onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                                                        disabled={pagination.page >= pagination.total_pages}
                                                        className="px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                                    >
                                                        Sonraki
                                                    </button>
                                                </div>
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>
                        )}

                        {activeTab === 'categories' && (
                            <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                                <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                                    <div>
                                        <h2 className="text-lg font-semibold text-gray-900">Kategori Görüntüleme İstatistikleri</h2>
                                        <p className="text-sm text-gray-500 mt-1">Kategorilerin görüntülenme sayıları ve benzersiz ziyaretçi sayıları</p>
                                    </div>
                                    <div className="flex items-center gap-3">
                                        <input
                                            type="text"
                                            placeholder="Ara..."
                                            value={searchTerm}
                                            onChange={(e) => setSearchTerm(e.target.value)}
                                            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
                                        />
                                        <button
                                            onClick={() => exportToCSV(filteredCategoryStats, 'kategori_istatistikleri')}
                                            className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition text-sm font-medium"
                                        >
                                            📥 Dışa Aktar
                                        </button>
                                    </div>
                                </div>
                                {categoryStats.length === 0 ? (
                                    <div className="text-center py-12">
                                        <p className="text-gray-500">Kategori istatistiği bulunamadı.</p>
                                    </div>
                                ) : (
                                    <>
                                        <div className="overflow-x-auto">
                                            <table className="min-w-full divide-y divide-gray-200">
                                                <thead className="bg-gray-50">
                                                    <tr>
                                                        <th 
                                                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100"
                                                            onClick={() => handleSort('category_id')}
                                                        >
                                                            <div className="flex items-center gap-2">
                                                                Kategori
                                                                {sortConfig.key === 'category_id' && (
                                                                    <span>{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>
                                                                )}
                                                            </div>
                                                        </th>
                                                        <th 
                                                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100"
                                                            onClick={() => handleSort('view_count')}
                                                        >
                                                            <div className="flex items-center gap-2">
                                                                Görüntülenme
                                                                {sortConfig.key === 'view_count' && (
                                                                    <span>{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>
                                                                )}
                                                            </div>
                                                        </th>
                                                        <th 
                                                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100"
                                                            onClick={() => handleSort('unique_visitors')}
                                                        >
                                                            <div className="flex items-center gap-2">
                                                                Benzersiz Ziyaretçi
                                                                {sortConfig.key === 'unique_visitors' && (
                                                                    <span>{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>
                                                                )}
                                                            </div>
                                                        </th>
                                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tarih</th>
                                                    </tr>
                                                </thead>
                                                <tbody className="bg-white divide-y divide-gray-200">
                                                    {filteredCategoryStats.map((stat, idx) => (
                                                        <tr key={idx} className="hover:bg-gray-50">
                                                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                                                {stat.category_name 
                                                                    ? `#${stat.category_id} ${stat.category_name}`
                                                                    : `#${stat.category_id}`}
                                                            </td>
                                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{stat.view_count}</td>
                                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{stat.unique_visitors}</td>
                                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{formatDate(stat.date)}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                        
                                        {/* Pagination */}
                                        {categoryPagination.total_pages > 1 && (
                                            <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
                                                <div className="text-sm text-gray-700">
                                                    Toplam <span className="font-medium">{categoryPagination.total}</span> kayıt
                                                </div>
                                                <div className="flex items-center space-x-2">
                                                    <button
                                                        onClick={() => setCategoryPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                                                        disabled={categoryPagination.page === 1}
                                                        className="px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                                    >
                                                        Önceki
                                                    </button>
                                                    <span className="text-sm text-gray-700">
                                                        Sayfa {categoryPagination.page} / {categoryPagination.total_pages}
                                                    </span>
                                                    <button
                                                        onClick={() => setCategoryPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                                                        disabled={categoryPagination.page >= categoryPagination.total_pages}
                                                        className="px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                                    >
                                                        Sonraki
                                                    </button>
                                                </div>
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>
                        )}

                        {activeTab === 'products' && (
                            <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                                <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                                    <div>
                                        <h2 className="text-lg font-semibold text-gray-900">Ürün Görüntüleme İstatistikleri</h2>
                                        <p className="text-sm text-gray-500 mt-1">Ürünlerin görüntülenme sayıları ve benzersiz ziyaretçi sayıları</p>
                                    </div>
                                    <div className="flex items-center gap-3">
                                        <input
                                            type="text"
                                            placeholder="Ara..."
                                            value={searchTerm}
                                            onChange={(e) => setSearchTerm(e.target.value)}
                                            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
                                        />
                                        <button
                                            onClick={() => exportToCSV(filteredProductStats, 'urun_istatistikleri')}
                                            className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition text-sm font-medium"
                                        >
                                            📥 Dışa Aktar
                                        </button>
                                    </div>
                                </div>
                                {productStats.length === 0 ? (
                                    <div className="text-center py-12">
                                        <p className="text-gray-500">Ürün istatistiği bulunamadı.</p>
                                    </div>
                                ) : (
                                    <>
                                        <div className="overflow-x-auto">
                                            <table className="min-w-full divide-y divide-gray-200">
                                                <thead className="bg-gray-50">
                                                    <tr>
                                                        <th 
                                                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100"
                                                            onClick={() => handleSort('product_id')}
                                                        >
                                                            <div className="flex items-center gap-2">
                                                                Ürün
                                                                {sortConfig.key === 'product_id' && (
                                                                    <span>{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>
                                                                )}
                                                            </div>
                                                        </th>
                                                        <th 
                                                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100"
                                                            onClick={() => handleSort('view_count')}
                                                        >
                                                            <div className="flex items-center gap-2">
                                                                Görüntülenme
                                                                {sortConfig.key === 'view_count' && (
                                                                    <span>{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>
                                                                )}
                                                            </div>
                                                        </th>
                                                        <th 
                                                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100"
                                                            onClick={() => handleSort('unique_visitors')}
                                                        >
                                                            <div className="flex items-center gap-2">
                                                                Benzersiz Ziyaretçi
                                                                {sortConfig.key === 'unique_visitors' && (
                                                                    <span>{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>
                                                                )}
                                                            </div>
                                                        </th>
                                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tarih</th>
                                                    </tr>
                                                </thead>
                                                <tbody className="bg-white divide-y divide-gray-200">
                                                    {filteredProductStats.map((stat, idx) => (
                                                        <tr key={idx} className="hover:bg-gray-50">
                                                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                                                {stat.product_name ? `#${stat.product_id} ${stat.product_name}` : `#${stat.product_id}`}
                                                            </td>
                                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{stat.view_count}</td>
                                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{stat.unique_visitors}</td>
                                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{formatDate(stat.date)}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                        
                                        {/* Pagination */}
                                        {productPagination.total_pages > 1 && (
                                            <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
                                                <div className="text-sm text-gray-700">
                                                    Toplam <span className="font-medium">{productPagination.total}</span> kayıt
                                                </div>
                                                <div className="flex items-center space-x-2">
                                                    <button
                                                        onClick={() => setProductPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                                                        disabled={productPagination.page === 1}
                                                        className="px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                                    >
                                                        Önceki
                                                    </button>
                                                    <span className="text-sm text-gray-700">
                                                        Sayfa {productPagination.page} / {productPagination.total_pages}
                                                    </span>
                                                    <button
                                                        onClick={() => setProductPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                                                        disabled={productPagination.page >= productPagination.total_pages}
                                                        className="px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                                    >
                                                        Sonraki
                                                    </button>
                                                </div>
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>
                        )}

                        {activeTab === 'devices' && (
                            <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                                <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                                    <div>
                                        <h2 className="text-lg font-semibold text-gray-900">Cihaz ve İşletim Sistemi İstatistikleri</h2>
                                        <p className="text-sm text-gray-500 mt-1">Ziyaretçilerin kullandığı cihazlar ve işletim sistemleri</p>
                                    </div>
                                    <div className="flex items-center gap-3">
                                        <input
                                            type="text"
                                            placeholder="Ara..."
                                            value={searchTerm}
                                            onChange={(e) => setSearchTerm(e.target.value)}
                                            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
                                        />
                                        <button
                                            onClick={() => exportToCSV(filteredDeviceStats, 'cihaz_istatistikleri')}
                                            className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition text-sm font-medium"
                                        >
                                            📥 Dışa Aktar
                                        </button>
                                    </div>
                                </div>
                                {deviceStats.length === 0 ? (
                                    <div className="text-center py-12">
                                        <p className="text-gray-500">Cihaz istatistiği bulunamadı.</p>
                                    </div>
                                ) : (
                                    <div className="overflow-x-auto">
                                        <table className="min-w-full divide-y divide-gray-200">
                                            <thead className="bg-gray-50">
                                                <tr>
                                                    <th 
                                                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100"
                                                        onClick={() => handleSort('device_type')}
                                                    >
                                                        <div className="flex items-center gap-2">
                                                            Cihaz
                                                            {sortConfig.key === 'device_type' && (
                                                                <span>{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>
                                                            )}
                                                        </div>
                                                    </th>
                                                    <th 
                                                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100"
                                                        onClick={() => handleSort('os')}
                                                    >
                                                        <div className="flex items-center gap-2">
                                                            İşletim Sistemi
                                                            {sortConfig.key === 'os' && (
                                                                <span>{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>
                                                            )}
                                                        </div>
                                                    </th>
                                                    <th 
                                                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100"
                                                        onClick={() => handleSort('count')}
                                                    >
                                                        <div className="flex items-center gap-2">
                                                            Görüntülenme
                                                            {sortConfig.key === 'count' && (
                                                                <span>{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>
                                                            )}
                                                        </div>
                                                    </th>
                                                    <th 
                                                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100"
                                                        onClick={() => handleSort('unique_visitors')}
                                                    >
                                                        <div className="flex items-center gap-2">
                                                            Benzersiz Ziyaretçi
                                                            {sortConfig.key === 'unique_visitors' && (
                                                                <span>{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>
                                                            )}
                                                        </div>
                                                    </th>
                                                </tr>
                                            </thead>
                                            <tbody className="bg-white divide-y divide-gray-200">
                                                {filteredDeviceStats.map((stat, idx) => (
                                                    <tr key={idx} className="hover:bg-gray-50">
                                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{stat.device_type}</td>
                                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{stat.os}</td>
                                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{stat.count}</td>
                                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{stat.unique_visitors}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </div>
                        )}

                        {activeTab === 'campaigns' && (
                            <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                                <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                                    <div>
                                        <h2 className="text-lg font-semibold text-gray-900">Kampanya Görüntüleme İstatistikleri</h2>
                                        <p className="text-sm text-gray-500 mt-1">Kampanyaların görüntülenme sayıları ve benzersiz ziyaretçi sayıları</p>
                                    </div>
                                    <div className="flex items-center gap-3">
                                        <input
                                            type="text"
                                            placeholder="Ara..."
                                            value={searchTerm}
                                            onChange={(e) => setSearchTerm(e.target.value)}
                                            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
                                        />
                                        <button
                                            onClick={() => exportToCSV(filteredCampaignStats, 'kampanya_istatistikleri')}
                                            className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition text-sm font-medium"
                                        >
                                            📥 Dışa Aktar
                                        </button>
                                    </div>
                                </div>
                                {campaignStats.length === 0 ? (
                                    <div className="text-center py-12">
                                        <p className="text-gray-500">Kampanya istatistiği bulunamadı.</p>
                                    </div>
                                ) : (
                                    <>
                                        <div className="overflow-x-auto">
                                            <table className="min-w-full divide-y divide-gray-200">
                                                <thead className="bg-gray-50">
                                                    <tr>
                                                        <th 
                                                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100"
                                                            onClick={() => handleSort('campaign_id')}
                                                        >
                                                            <div className="flex items-center gap-2">
                                                                Kampanya
                                                                {sortConfig.key === 'campaign_id' && (
                                                                    <span>{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>
                                                                )}
                                                            </div>
                                                        </th>
                                                        <th 
                                                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100"
                                                            onClick={() => handleSort('view_count')}
                                                        >
                                                            <div className="flex items-center gap-2">
                                                                Görüntülenme
                                                                {sortConfig.key === 'view_count' && (
                                                                    <span>{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>
                                                                )}
                                                            </div>
                                                        </th>
                                                        <th 
                                                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100"
                                                            onClick={() => handleSort('unique_visitors')}
                                                        >
                                                            <div className="flex items-center gap-2">
                                                                Benzersiz Ziyaretçi
                                                                {sortConfig.key === 'unique_visitors' && (
                                                                    <span>{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>
                                                                )}
                                                            </div>
                                                        </th>
                                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tarih</th>
                                                    </tr>
                                                </thead>
                                                <tbody className="bg-white divide-y divide-gray-200">
                                                    {filteredCampaignStats.map((stat, idx) => (
                                                        <tr key={idx} className="hover:bg-gray-50">
                                                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                                                {stat.campaign_name ? `#${stat.campaign_id} ${stat.campaign_name}` : `#${stat.campaign_id}`}
                                                            </td>
                                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{stat.view_count}</td>
                                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{stat.unique_visitors}</td>
                                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{formatDate(stat.date)}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                        
                                        {/* Pagination */}
                                        {campaignPagination.total_pages > 1 && (
                                            <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
                                                <div className="text-sm text-gray-700">
                                                    Toplam <span className="font-medium">{campaignPagination.total}</span> kayıt
                                                </div>
                                                <div className="flex items-center space-x-2">
                                                    <button
                                                        onClick={() => setCampaignPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                                                        disabled={campaignPagination.page === 1}
                                                        className="px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                                    >
                                                        Önceki
                                                    </button>
                                                    <span className="text-sm text-gray-700">
                                                        Sayfa {campaignPagination.page} / {campaignPagination.total_pages}
                                                    </span>
                                                    <button
                                                        onClick={() => setCampaignPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                                                        disabled={campaignPagination.page >= campaignPagination.total_pages}
                                                        className="px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                                    >
                                                        Sonraki
                                                    </button>
                                                </div>
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>
                        )}

                        {activeTab === 'actions' && (
                            <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                                <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                                    <div>
                                        <h2 className="text-lg font-semibold text-gray-900">Aksiyon İstatistikleri</h2>
                                        <p className="text-sm text-gray-500 mt-1">WhatsApp, telefon ve iletişim tıklamaları</p>
                                    </div>
                                    <div className="flex items-center gap-3">
                                        <input
                                            type="text"
                                            placeholder="Ara..."
                                            value={searchTerm}
                                            onChange={(e) => setSearchTerm(e.target.value)}
                                            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
                                        />
                                        <button
                                            onClick={() => exportToCSV(filteredActionStats, 'aksiyon_istatistikleri')}
                                            className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition text-sm font-medium"
                                        >
                                            📥 Dışa Aktar
                                        </button>
                                    </div>
                                </div>
                                {actionStats.length === 0 ? (
                                    <div className="text-center py-12">
                                        <p className="text-gray-500">Aksiyon istatistiği bulunamadı.</p>
                                    </div>
                                ) : (
                                    <>
                                        <div className="overflow-x-auto">
                                            <table className="min-w-full divide-y divide-gray-200">
                                                <thead className="bg-gray-50">
                                                    <tr>
                                                        <th 
                                                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100"
                                                            onClick={() => handleSort('entity_type')}
                                                        >
                                                            <div className="flex items-center gap-2">
                                                                Aksiyon Tipi
                                                                {sortConfig.key === 'entity_type' && (
                                                                    <span>{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>
                                                                )}
                                                            </div>
                                                        </th>
                                                        <th 
                                                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100"
                                                            onClick={() => handleSort('count')}
                                                        >
                                                            <div className="flex items-center gap-2">
                                                                Tıklama
                                                                {sortConfig.key === 'count' && (
                                                                    <span>{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>
                                                                )}
                                                            </div>
                                                        </th>
                                                        <th 
                                                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100"
                                                            onClick={() => handleSort('unique_clicks')}
                                                        >
                                                            <div className="flex items-center gap-2">
                                                                Benzersiz Tıklama
                                                                {sortConfig.key === 'unique_clicks' && (
                                                                    <span>{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>
                                                                )}
                                                            </div>
                                                        </th>
                                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tarih</th>
                                                    </tr>
                                                </thead>
                                                <tbody className="bg-white divide-y divide-gray-200">
                                                    {filteredActionStats.map((stat, idx) => (
                                                        <tr key={idx} className="hover:bg-gray-50">
                                                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{getEntityTypeLabel(stat.entity_type)}</td>
                                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{stat.count}</td>
                                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{stat.unique_clicks}</td>
                                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{formatDate(stat.date)}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                        
                                        {/* Pagination */}
                                        {actionPagination.total_pages > 1 && (
                                            <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
                                                <div className="text-sm text-gray-700">
                                                    Toplam <span className="font-medium">{actionPagination.total}</span> kayıt
                                                </div>
                                                <div className="flex items-center space-x-2">
                                                    <button
                                                        onClick={() => setActionPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                                                        disabled={actionPagination.page === 1}
                                                        className="px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                                    >
                                                        Önceki
                                                    </button>
                                                    <span className="text-sm text-gray-700">
                                                        Sayfa {actionPagination.page} / {actionPagination.total_pages}
                                                    </span>
                                                    <button
                                                        onClick={() => setActionPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                                                        disabled={actionPagination.page >= actionPagination.total_pages}
                                                        className="px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                                    >
                                                        Sonraki
                                                    </button>
                                                </div>
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>
                        )}
                    </>
                )}
            </div>
        </AdminLayout>
    );
};

export default AnalyticsPage;
